import React from 'react';
import PropTypes from 'prop-types';

import { TooltipContent, TooltipToggle } from 'components/Tooltip';
import { bem } from 'helpers/class-name';

import './SegmentedCapacityBar.scss';

const { block, element } = bem('segmented-capacity-bar');

export const SEGMENTED_CAPACITY_BAR_SEGMENT = {
  CLASSPASS: 'classpass',
  DIRECT: 'direct',
  UNFILLED: 'unfilled',
};

const DEFAULT_BAR_NAME = 'segmented-capacity-bar';
const FILL_BAR_EMPTY_VALUE = 0;
const FILL_BAR_FULL_PERCENT = 100;

const legendItemShape = PropTypes.shape({
  accessory: PropTypes.node,
  key: PropTypes.oneOf(Object.values(SEGMENTED_CAPACITY_BAR_SEGMENT)).isRequired,
  label: PropTypes.node.isRequired,
});

function getVisibleSegments(segments) {
  return segments.filter(({ value }) => value > FILL_BAR_EMPTY_VALUE);
}

function getTrackTotal(segments) {
  return segments.reduce((sum, { value }) => sum + value, FILL_BAR_EMPTY_VALUE);
}

export function SegmentedCapacityBarLegend({ items = [] }) {
  if (!items.length) {
    return null;
  }

  return (
    <div className={element('legend')}>
      {items.map(({ accessory, key, label }) => (
        <span className={element('legend-item')} key={key}>
          <span className={element('swatch', { [key]: true })} />
          <span className={element('legend-label')}>{label}</span>
          {accessory}
        </span>
      ))}
    </div>
  );
}

SegmentedCapacityBarLegend.propTypes = {
  items: PropTypes.arrayOf(legendItemShape),
};

function CapacityBarSegment({ name, segment }) {
  const { key, tooltip, value } = segment;
  const className = element('segment', { [key]: true });
  const style = { flexGrow: value };

  if (!tooltip) {
    return <span className={className} style={style} />;
  }

  const tooltipName = `${name}-${key}`;

  return (
    <TooltipToggle className={className} name={tooltipName} style={style}>
      <TooltipContent hoverable name={tooltipName}>
        {tooltip}
      </TooltipContent>
    </TooltipToggle>
  );
}

CapacityBarSegment.propTypes = {
  name: PropTypes.string.isRequired,
  segment: PropTypes.shape({
    key: PropTypes.oneOf(Object.values(SEGMENTED_CAPACITY_BAR_SEGMENT)).isRequired,
    tooltip: PropTypes.node,
    value: PropTypes.number.isRequired,
  }).isRequired,
};

const SegmentedCapacityBar = ({
  className,
  legendAction = null,
  legendItems = [],
  muted = false,
  name = DEFAULT_BAR_NAME,
  segments = [],
}) => {
  const visibleSegments = getVisibleSegments(segments);
  const trackTotal = getTrackTotal(visibleSegments);
  const renderedSegments =
    trackTotal > FILL_BAR_EMPTY_VALUE
      ? visibleSegments
      : [{ key: SEGMENTED_CAPACITY_BAR_SEGMENT.UNFILLED, value: FILL_BAR_FULL_PERCENT }];

  return (
    <div className={block({ muted }, className)}>
      <div className={element('track')}>
        {renderedSegments.map(segment => (
          <CapacityBarSegment key={segment.key} name={name} segment={segment} />
        ))}
      </div>
      {legendAction ? (
        <div className={element('legend-row')}>
          <SegmentedCapacityBarLegend items={legendItems} />
          {legendAction}
        </div>
      ) : (
        <SegmentedCapacityBarLegend items={legendItems} />
      )}
    </div>
  );
};

SegmentedCapacityBar.propTypes = {
  className: PropTypes.string,
  legendAction: PropTypes.node,
  legendItems: PropTypes.arrayOf(legendItemShape),
  muted: PropTypes.bool,
  name: PropTypes.string,
  segments: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOf(Object.values(SEGMENTED_CAPACITY_BAR_SEGMENT)).isRequired,
      tooltip: PropTypes.node,
      value: PropTypes.number.isRequired,
    })
  ),
};

export default SegmentedCapacityBar;
